import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, Youtube, ArrowRight } from "lucide-react";

export default function Footer() {
    const [logoOk, setLogoOk] = useState(true);
    return (
        <footer data-testid="footer" className="relative overflow-hidden bg-coal/60">
            <div className="relative" aria-hidden="true">
                <div
                    className="frayed-hem denim-bg relative h-14 md:h-20"
                    style={{ backgroundImage: "url('/img/denim/denim-texture.jpg')" }}
                >
                    <div className="absolute inset-0 bg-obsidian/40" />
                </div>
                <span className="thread left-[10%] top-[58%]" />
                <span className="thread left-[34%] top-[66%] rotate-[-8deg]" />
                <span className="thread left-[57%] top-[55%]" />
                <span className="thread left-[82%] top-[64%] rotate-[-10deg]" />
            </div>
            <div className="pointer-events-none select-none px-4 pt-10 text-center">
                <span className="font-display text-[23vw] font-extrabold uppercase leading-[0.78] text-stroke-faint">
                    URWAN
                </span>
            </div>
            <div className="mx-auto grid w-full max-w-[1400px] gap-12 px-5 py-16 md:grid-cols-12 md:px-8 lg:px-10">
                <div className="md:col-span-4">
                    {logoOk ? (
                        <img
                            src="/img/brand/logo.png"
                            alt="URWAN"
                            onError={() => setLogoOk(false)}
                            className="h-10 w-auto"
                        />
                    ) : (
                        <span className="font-display text-3xl font-extrabold text-ink">URWAN</span>
                    )}
                    <p className="mt-5 max-w-xs text-sm leading-relaxed text-mute">
                        Minimalist, performance-driven denim engineered in Bangalore — made to be worn,
                        lived in, and valued for years.
                    </p>
                    <div className="mt-6 flex gap-3">
                        {[
                            { icon: Instagram, id: "instagram", href: "https://www.instagram.com/urwandenims/" },
                            { icon: Facebook, id: "facebook", href: null },
                            { icon: Youtube, id: "youtube", href: null },
                        ].map(({ icon: Icon, id, href }) => (
                            <a
                                key={id}
                                href={href || "#"}
                                target={href ? "_blank" : undefined}
                                rel={href ? "noopener noreferrer" : undefined}
                                onClick={href ? undefined : (e) => e.preventDefault()}
                                data-testid={`footer-social-${id}`}
                                aria-label={href ? `URWAN on ${id}` : `${id} coming soon`}
                                className="grid h-10 w-10 place-items-center border border-white/15 text-mute transition-colors hover:border-gold hover:bg-gold hover:text-black"
                            >
                                <Icon size={16} />
                            </a>
                        ))}
                    </div>
                </div>
                <div className="md:col-span-2">
                    <h4 className="text-[11px] font-mono uppercase tracking-[0.25em] text-gold">Explore</h4>
                    <ul className="mt-5 space-y-3">
                        {[
                            ["Home", "/"],
                            ["Collection", "/shop"],
                            ["About Us", "/about"],
                            ["Contact Us", "/contact"],
                            ["My Account", "/account"],
                        ].map(([label, to]) => (
                            <li key={to}>
                                <Link
                                    to={to}
                                    data-testid={`footer-link-${to === "/" ? "home" : to.slice(1)}`}
                                    className="text-sm text-mute transition-colors hover:text-gold"
                                >
                                    {label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="md:col-span-3">
                    <h4 className="text-[11px] font-mono uppercase tracking-[0.25em] text-gold">Fresh In</h4>
                    <div className="mt-5 flex gap-4">
                        <img
                            src="/img/urwan/2J7A0742.jpg"
                            alt="Vintage Denim Shirt – Ice Blue"
                            className="h-24 w-20 shrink-0 object-cover"
                        />
                        <div>
                            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold">Fresh In</p>
                            <p className="mt-1 font-display text-sm font-semibold text-ink">
                                Vintage Denim Shirt – Ice Blue
                            </p>
                            <Link
                                to="/shop?line=everyday"
                                data-testid="footer-discover"
                                className="group mt-2 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-mute transition-colors hover:text-gold"
                            >
                                Discover
                                <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
                            </Link>
                        </div>
                    </div>
                </div>
                <div className="md:col-span-3">
                    <h4 className="text-[11px] font-mono uppercase tracking-[0.25em] text-gold">Contact</h4>
                    <ul className="mt-5 space-y-4 text-sm text-mute">
                        <li className="flex items-start gap-3">
                            <Phone size={16} className="mt-0.5 shrink-0 text-gold" />
                            <a href="tel:9738002530" className="transition-colors hover:text-gold">
                                97380 02530
                            </a>
                        </li>
                        <li className="flex items-start gap-3">
                            <Mail size={16} className="mt-0.5 shrink-0 text-gold" />
                            <a href="mailto:info@urwan.com" className="transition-colors hover:text-gold">
                                info@urwan.com
                            </a>
                        </li>
                        <li className="flex items-start gap-3">
                            <MapPin size={16} className="mt-0.5 shrink-0 text-gold" />
                            <span>
                                C011, SSVR Urban Flora, Immadihalli Main Road, Whitefield, Bangalore – 560066
                            </span>
                        </li>
                    </ul>
                </div>
            </div>
            <div className="flex flex-col items-center justify-between gap-3 border-t border-white/10 px-6 py-6 sm:flex-row lg:px-10">
                <p className="text-xs text-mute">Copyright 2026 © Urwan. All Rights Reserved.</p>
                <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-mute">
                    Luxury Denim · Woven & Engineered in India
                </p>
            </div>
        </footer>
    );
}
